import {
    Button,
    FormControl,
    FormHelperText,
    FormLabel,
    Input,
    Link,
    Option,
    Select,
    Stack,
    Typography,
} from '@mui/joy';
import React, { useEffect, useState } from 'react';
import { serviceFacade } from '../services/service-facade';
import { recentRoundsService } from '../services/recent-rounds-service';
import { getMyIdentity } from '../services/player-identity-service';
import { useNavigate } from 'react-router';
import { CourseSummary } from '../models/course';

const CreateRound = () => {
    const [roundName, setRoundName] = useState('');
    const [playerName, setPlayerName] = useState(() => getMyIdentity()?.name ?? '');
    const [courseId, setCourseId] = useState<string | null>(null);
    const [courses, setCourses] = useState<CourseSummary[]>([]);
    const [favoriteIds, setFavoriteIds] = useState<string[]>([]);
    const [showAllCourses, setShowAllCourses] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [roundNameTouched, setRoundNameTouched] = useState(false);
    const [playerNameTouched, setPlayerNameTouched] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        serviceFacade
            .listCourses()
            .then(setCourses)
            .catch(() => {});

        const identity = getMyIdentity();
        if (identity) {
            serviceFacade
                .getFavoriteCourseIds(identity.id)
                .then(setFavoriteIds)
                .catch(() => {});
        }
    }, []);

    const favoriteCourses = courses.filter((c) => favoriteIds.includes(c.id));
    const coursesToShow = showAllCourses || favoriteCourses.length === 0 ? courses : favoriteCourses;

    const roundNameEmpty = !roundName.trim();
    const playerNameEmpty = !playerName.trim();
    const isSubmitDisabled = isLoading || roundNameEmpty || playerNameEmpty;

    const handleStartRound = async () => {
        try {
            setIsLoading(true);
            const response = await serviceFacade.createRound(roundName, playerName, courseId ?? undefined);
            const round = response.body.round;
            const player = response.body.player;
            recentRoundsService.updateRecentRounds(
                round.id,
                round.name,
                round.date,
                player.id,
                player.name,
                round.expires_at
            );
            navigate(`/${round.id}/${player.id}`);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Stack spacing={4}>
            <Typography level="h2">Create New Round</Typography>
            <FormControl error={roundNameTouched && roundNameEmpty}>
                <FormLabel>Round Name</FormLabel>
                <Input
                    placeholder="Enter round name"
                    value={roundName}
                    onChange={(e) => setRoundName(e.target.value)}
                    onBlur={() => setRoundNameTouched(true)}
                />
                {roundNameTouched && roundNameEmpty && <FormHelperText>Round Name is required</FormHelperText>}
            </FormControl>
            <FormControl error={playerNameTouched && playerNameEmpty}>
                <FormLabel>Player Name</FormLabel>
                <Input
                    placeholder="Enter your name"
                    value={playerName}
                    onChange={(e) => setPlayerName(e.target.value)}
                    onBlur={() => setPlayerNameTouched(true)}
                />
                {playerNameTouched && playerNameEmpty && <FormHelperText>Player Name is required</FormHelperText>}
            </FormControl>
            <FormControl>
                <FormLabel>Course (optional)</FormLabel>
                <Select placeholder="No course" value={courseId} onChange={(_, value) => setCourseId(value)}>
                    <Option value={null}>No course</Option>
                    {coursesToShow.map((course) => (
                        <Option key={course.id} value={course.id}>
                            {course.name}
                        </Option>
                    ))}
                </Select>
                {!showAllCourses && favoriteCourses.length > 0 && (
                    <FormHelperText>
                        <Link component="button" type="button" onClick={() => setShowAllCourses(true)}>
                            Browse all courses
                        </Link>
                    </FormHelperText>
                )}
            </FormControl>
            <Button onClick={handleStartRound} loading={isLoading} disabled={isSubmitDisabled}>
                Start Round
            </Button>
        </Stack>
    );
};
export default CreateRound;
